<div
  class="flex h-20 items-center justify-around gap-3 !py-4"
  [class.!justify-between]="topUpDirective.topUpPreselectedAmountInCents().length > 3"
  role="group"
  [attr.aria-label]="'prepaid-account-top-up-modal.amount-selection' | translate"
>
  @if (freeAmountButtonPosition() === 'first') {
    <app-button-square-atom
      [isActive]="!topUpDirective.hasSelectedAPresetAmount()"
      [text]="'prepaid-account-top-up-modal.other-amount' | translate"
      class="font-bold"
      role="button"
      tabindex="0"
      (click)="topUpDirective.onSelectedAmount()"
      (keydown.enter)="topUpDirective.onSelectedAmount()"
      (keydown.space)="topUpDirective.onSelectedAmount()"
    ></app-button-square-atom>
  }

  @for (amount of topUpDirective.topUpPreselectedAmountInCents(); track amount) {
    <app-button-square-atom
      [isActive]="topUpDirective.selectedAmountInCents()() === amount"
      [text]="formatPrice(amount)"
      [attr.aria-pressed]="topUpDirective.selectedAmountInCents()() === amount"
      [attr.aria-label]="
        'prepaid-account-top-up-modal.select-amount' | translate: { amount: formatPrice(amount) }
      "
      role="button"
      tabindex="0"
      (click)="topUpDirective.onSelectedAmount(amount)"
      (keydown.enter)="topUpDirective.onSelectedAmount(amount)"
      (keydown.space)="topUpDirective.onSelectedAmount(amount)"
    ></app-button-square-atom>
  }

  @if (freeAmountButtonPosition() === 'last') {
    <app-button-square-atom
      [isActive]="!topUpDirective.hasSelectedAPresetAmount()"
      [text]="'prepaid-account-top-up-modal.other-amount' | translate"
      class="font-bold"
      role="button"
      tabindex="0"
      (click)="topUpDirective.onSelectedAmount()"
      (keydown.enter)="topUpDirective.onSelectedAmount()"
      (keydown.space)="topUpDirective.onSelectedAmount()"
    ></app-button-square-atom>
  }
</div>

<!-- Instructions pour saisir un montant -->
<div
  class="px-2 text-left text-sm text-gray-600 mb-2"
  role="status"
>
  {{
    'prepaid-account-top-up-modal.custom-amount-placeholder'
      | translate
        : {
            min: formatPrice(topUpDirective.minTopUpAmount() ?? 0),
            max: formatPrice(topUpDirective.maxTopUpAmount() ?? 0),
          }
  }}
</div>

<div class="w-full">
  <div class="relative">
    <input
      #priceInput
      id="price-to-top-up"
      type="number"
      name="price-to-top-up"
      inputmode="decimal"
      class="w-full rounded px-4.5 py-5 pr-12 text-center font-bold focus:outline-none"
      [class.border]="topUpDirective.isAmountValid()"
      [class.border-neutral-300]="topUpDirective.isAmountValid()"
      [class.bg-neutral-50]="topUpDirective.isAmountValid()"
      [class.text-neutral-700]="topUpDirective.isAmountValid()"
      [class.focus:border-primary-500]="topUpDirective.isAmountValid()"
      [class.focus:ring-1]="topUpDirective.isAmountValid()"
      [class.focus:ring-primary-200]="topUpDirective.isAmountValid()"
      [class.border-error-500]="!topUpDirective.isAmountValid()"
      [class.bg-error-50]="!topUpDirective.isAmountValid()"
      [class.text-error-700]="!topUpDirective.isAmountValid()"
      [class.focus:border-error-600]="!topUpDirective.isAmountValid()"
      [class.focus:ring-error-200]="!topUpDirective.isAmountValid()"
      [placeholder]="'prepaid-account-top-up-modal.input-placeholder' | translate"
      [attr.aria-label]="'prepaid-account-top-up-modal.custom-amount' | translate"
      [min]="(topUpDirective.minTopUpAmount() ?? 0) / 100"
      [max]="(topUpDirective.maxTopUpAmount() ?? 0) / 100"
      [value]="(topUpDirective.selectedAmountInCents()() ?? 0) / 100 || ''"
      (input)="topUpDirective.onInputChange($event)"
      [attr.aria-invalid]="!topUpDirective.isAmountValid()"
      [attr.aria-describedby]="topUpDirective.errorMessage() ? 'amount-error' : null"
    />
    <span
      class="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none font-bold"
      [class.text-neutral-700]="topUpDirective.isAmountValid()"
      [class.text-error-700]="!topUpDirective.isAmountValid()"
      aria-hidden="true"
    >
      {{ topUpDirective.currencySymbol() }}
    </span>
  </div>
  <div
    [class.invisible]="!topUpDirective.errorMessage()"
    id="amount-error"
    class="min-h-5 px-2 py-2 text-left text-sm text-error-600"
    role="alert"
    aria-live="polite"
  >
    {{ topUpDirective.errorMessage() }}
  </div>
</div>

<!-- Message d'information sur la recharge -->
<div
  class="px-2 text-left text-sm text-gray-600 mb-6"
  role="status"
>
  {{ 'prepaid-account-top-up-modal.recharge-info' | translate }}
</div>

<div class="flex items-center justify-center gap-5">
  @if (!topUpDirective.preSelectedAmountInCents()) {
    <app-button-atom
      class="block flex-1"
      [label]="'prepaid-account-top-up-modal.cancel' | translate"
      [type]="'outlined'"
      [color]="'primary'"
      [full]="true"
      (click)="topUpDirective.cancel()"
    />
  } @else {
    <app-button-atom
      class="block flex-1"
      [label]="'prepaid-account-top-up-modal.remove' | translate"
      icon="icon-garbage"
      [type]="'light'"
      [color]="'neutral'"
      [full]="true"
      (click)="topUpDirective.remove()"
    />
  }
  <app-button-atom
    class="block flex-1"
    [label]="
      ('prepaid-account-top-up-modal.credit' | translate) +
      (topUpDirective.selectedAmountInCents()()
        ? ' ' + formatPrice(topUpDirective.selectedAmountInCents()() ?? 0)
        : '')
    "
    [disabled]="!topUpDirective.selectedAmountInCents()() || !topUpDirective.isAmountValid()"
    [type]="'primary'"
    [color]="'primary'"
    [full]="true"
    (click)="topUpDirective.onTopUp()"
  />
</div>
